import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { catchError, map, switchMap } from 'rxjs/operators';
import { of } from 'rxjs/observable/of';

import { LocalStorageUtils, UrlUtils } from '@blcloud/bl-common';
import {
  BlLoadingService,
  BlFormGroup,
  BlValidatorService,
  Rules,
  BlDialogService,
} from '@blcloud/bl-ng-ui-component';
import {
  DeviceSessionService,
  LoginResourceService,
  ScreenService,
} from '@blcloud/bl-ng-share-module';

import { LoginPageService } from './login-page.service';

@Component({
  selector: 'app-login-page',
  templateUrl: './login-page.component.html',
  styleUrls: ['./login-page.component.scss'],
  providers: [LoginPageService],
})
export class LoginPageComponent implements OnInit, OnDestroy {

  @Input() userId: string;
  @Input() password: string;

  message: string;
  logout: string;
  isInitCheck = false;
  returnUrl: string = null;

  /** 二重ログイン処理防止フラグ */
  private isLoginProcessing = false;

  /** 入力検証オブジェクト */
  form = new BlFormGroup();

  /** ログイン直後かどうかのローカルストレージキー */
  private readonly IMMEDIATELY_AFTER_LOGIN_STORAGE_KEY = 'immediatelyAfterLogin';

  constructor(
    private title: Title,
    private router: Router,
    private route: ActivatedRoute,
    private userLoginInfo: LoginResourceService,
    private loginPageService: LoginPageService,
    private loading: BlLoadingService,
    private deviceSessionSession: DeviceSessionService,
    private screenService: ScreenService,
    private blValidatorService: BlValidatorService,
    private blDialogService: BlDialogService,
  ) {
    this.title.setTitle('ログインページ');
  }

  /**
   * 初期化
   */
  ngOnInit() {
    this.screenService.hideFrame();
    this.returnUrl = null;
    this.route.queryParams.subscribe(params => {
      if (params.logout !== undefined) {
        this.logout = 'ログアウトしました。';
      }
      if (params.timeout !== undefined) {
        this.logout = 'セッションの有効期限が切れました。';
      }
      if (params.error !== undefined) {
        this.logout = '認証情報の異常のため、ログアウトしました。';
      }
      if (
        params.rtnUrl !== undefined &&
        !params.rtnUrl.split('?')[0].match(/menu\/login$/) && // ログイン画面へのリダイレクトは防止する
        UrlUtils.isSafeReturnUrl(params.rtnUrl) // javascript:等の危険なスキームや他ドメインへの遷移を防止する
      ) {
        this.returnUrl = params.rtnUrl;
      }
    });

    this.loginPageService.logout().subscribe();

    // 検証用のルールを取得
    this.loginPageService.rules().subscribe(
      (rules: Rules[]) => {
        this.form = this.blValidatorService.getBlFormGroup(rules);
      },
      () => {
        // ログイン画面にリダイレクトされる場合があるので新規エラーをスルーする。
        throw new Error('検証用ルールが取得できません。');
      }
    );
    // TODO: 直接背景を設定する方法がないため、selectorで上書き
    document.querySelector('body').style.backgroundColor = '#efefef';
  }

  ngOnDestroy() {
    // NOTE: ngOnInitでbodyの背景色を設定しているので削除
    document.querySelector('body').style.backgroundColor = '';
  }

  /**
   * ログイン処理
   */
  onLogin() {
    if (this.isLoginProcessing) {
      return;
    }
    this.isLoginProcessing = true;
    this.tryLogin();
  }

  /**
   * chromeのダウンロードページへのリンク
   * @param event
   */
  onOpenChrome(_event: any) {
    window.open('https://www.google.co.jp/chrome/browser/desktop/index.html', '_blank');
  }

  setMessage(value: string) {
    this.message = value;
  }

  onChangeUserId(event: any): void {
    this.userId = this.getInputValue(event);
  }

  onChangePassword(event: any): void {
    this.password = this.getInputValue(event);
  }

  /**
   * パスワードNextFocus時処理
   * @param value 入力値
   */
  onPasswordInputEnter(event: KeyboardEvent) {
    const submit = <HTMLInputElement>document.querySelector('#submit');
    submit.focus();
    /**
     * TODO: 強制的にフォーカスアウトすると、clickも発火するのでコメントアウト。
     * 今後、BlFocusComponentの仕様が変わった際に動かなくなる可能性があるのでTODOを残す。
     */
    // submit.click();
    event.stopPropagation();
  }

  /**
   * ログインエラーメッセージを表示します。
   */
  private showLoginErrorMessage() {
    this.blDialogService.info('ただいまサーバが混み合っております。 しばらくたってからもう一度お試しください').subscribe(
      dialogRef => dialogRef.hide()
    );
  }

  /**
   * 入力フォームの値を取得
   * @param event イベント
   * @returns 入力フォームの値
   */
  private getInputValue(event: any): string {
    if (typeof event === 'string') {
      return event;
    }
    if (event == null) {
      return '';
    }
    if (typeof event.value === 'string') {
      return event.value;
    }
    if (event.target && typeof event.target.value === 'string') {
      return event.target.value;
    }
    return '';
  }

  /**
   * パスワード入力フォームのDOMの値を直接取得
   * @returns パスワード入力フォームの値
   */
  private getPasswordValueFromDom(): string {
    const passwordInput = document.querySelector(
      'input.password-input'
    ) as HTMLInputElement | null;

    if (!passwordInput || typeof passwordInput.value !== 'string') {
      return '';
    }
    return passwordInput.value;
  }

  /**
   * パスワード入力フォームのフォームの値を取得
   * @returns パスワード入力フォームのフォームの値
   */
  private getPasswordValueFromForm(): string {
    const formGroup: any = this.form && this.form.formGroup;
    if (!formGroup) {
      return '';
    }

    const candidateKeys = ['UserLoginInfo#password', 'password'];

    for (const key of candidateKeys) {
      const control = formGroup.controls && formGroup.controls[key];
      if (control && typeof control.value === 'string' && control.value) {
        return control.value;
      }
    }

    const value = formGroup.value || {};
    for (const key of candidateKeys) {
      if (typeof value[key] === 'string' && value[key]) {
        return value[key];
      }
    }

    return '';
  }

  /**
   * ログイン試行
   */
  private tryLogin(): void {
    const isPasswordEmpty = !this.password;
    const isUserIdPresent = !!this.userId;

    // userIdが入力済みでpasswordが空の場合、DOMに入っている値があれば優先して反映する
    if (isPasswordEmpty && isUserIdPresent) {
      const passwordInForm = this.getPasswordValueFromForm();
      if (passwordInForm) {
        // フォームの値があればそれを反映
        this.password = passwordInForm;
      } else {
        // フォームの値がなければDOMの値を反映
        const passwordInDom = this.getPasswordValueFromDom();
        if (passwordInDom) {
          this.password = passwordInDom;
        }
      }
    }

    // 入力値検証
    if (!this.form.validate()) {
      this.isLoginProcessing = false;
      return;
    }

    this.setMessage('ログイン中');
    this.loading.show();
    this.logout = null;
    this.userLoginInfo.login(this.userId, this.password).subscribe(
      (res) => {
        this.loading.hide();
        // ログイン画面に遷移
        if (res instanceof Array) {
          this.loginPageService.setLoginCookie(res).subscribe(
            () => {
              // ログインできたら端末セッションの登録を行う
              try {
                this.deviceSessionSession.createDeviceSession().subscribe(
                  (success) => {
                    console.log(success);
                    LocalStorageUtils.save(this.IMMEDIATELY_AFTER_LOGIN_STORAGE_KEY, true);
                    // 利用者認証情報を取得
                    this.loginPageService.getUserAuthInfo(this.userId).pipe(
                      switchMap((authInfo) => {
                        // 利用者認証情報.パスワード変更要求フラグ
                        const requiresPassWordChangeFlg = authInfo.requiresPassWordChangeFlg;
                        const blUserId = authInfo ? authInfo.blUserId : null;
                        // パスワード変更要求フラグがtrueかつbluserIDがある場合はパスワード変更画面に遷移
                        if (requiresPassWordChangeFlg && blUserId) {
                          return this.loginPageService.getUserEmail(blUserId).pipe(
                              switchMap((email: string | null) => {
                                if (!email) {
                                  throw new Error(
                                    'メールアドレスが取得できませんでした',
                                  );
                                }
                                // パスワード変更チケットを発行する
                                return this.loginPageService.issuePasswordChangeTicket(email, requiresPassWordChangeFlg);
                              }),
                              map((token: string) => {
                                return {
                                  isPasswordChange: true,
                                  token: token,
                                  requiresPassWordChangeFlg:
                                  requiresPassWordChangeFlg,
                                };
                              }),
                            );
                        } // 強制変更が不要な場合は、次の処理（通常遷移）に進むためのシグナルを流す
                        return of({ isPasswordChange: false });
                      }),
                      catchError((err) => {
                        console.error(
                          '認証情報取得またはチケット発行でエラーが発生しました:',
                          err,
                        ); // 万が一エラーでもログイン不可にならないよう通常ログインする
                        return of({ isPasswordChange: false });
                      }),
                    )
                    .subscribe({
                      next: (r: any) => {
                        this.loading.hide(); // 通常ログイン
                        // ログイン成功後は遷移するため、ガード解除は保守的に行う
                        this.isLoginProcessing = false;
                        if (r.isPasswordChange) {
                          // 強制的にパスワード変更画面に遷移
                          localStorage.setItem('isPasswordChangeRequired', 'true');
                          const encodedId = encodeURIComponent(this.userId);
                          const targetPath = '/contract/password/password-setting-page';
                          // tslint:disable-next-line: max-line-length
                          window.location.href = `${targetPath}?accountId=${encodedId}&token=${r.token}&requiresPassWordChangeFlg=${r.requiresPassWordChangeFlg}`;
                        } else {
                          // 通常ログイン
                          if (this.returnUrl) {
                            window.open(this.returnUrl, '_self'); // 指定されたリダイレクト先に遷移
                          } else {
                            // ログイン後初回フラグを書き込み
                            this.loginPageService.saveLocalStorageInitAfterLogin(
                              true,
                            );
                            this.router.navigate(['biz']);
                          }
                        }
                      },
                      error: (err) => {
                        this.isLoginProcessing = false;
                        console.log(err);
                      },
                    });
                  },
                  (err) => {
                    this.isLoginProcessing = false;
                    console.log(err);
                  },
                );
              } catch (err) {
                this.isLoginProcessing = false;
                console.log(err);
              }
            },
          );
        } else {
          this.isLoginProcessing = false;
          // 汎用エラー画面に遷移
          throw new Error('配列ではありません。');
        }
      },
      (_e) => {
        this.loading.hide();
        this.password = null;
        this.form.reset();
        this.isLoginProcessing = false;

        if (_e.status === 401) {
          const result = _e.messages[0].split(',');
          if (result[0] === 'E00067') {
            this.setMessage(
              'ログインIDまたはパスワードが正しくありません。\nもう一度お試しください。',
            );
          } else if (result[0] === 'E00544') {
            this.setMessage(
              'アカウントが見つかりませんでした。\nアカウントの作成が完了しているかご確認ください。',
            );
          } else if (result[0] === 'E00142') {
            this.setMessage(
              'パスワード認証に失敗したため、パスワードが無効となりました。パスワードの再設定を行ってください。',
            );
          } else if (result[0] === 'E00543') {
            this.setMessage('当該アカウントは、現在ご利用いただけません。');
          } else if (result[0] === 'E00545') {
            this.setMessage('アクセスが制限されています。');
          } else {
            this.showLoginErrorMessage();
          }
        } else {
          this.showLoginErrorMessage();
        }
      },
    );
  }

}




// WEBPACK FOOTER //
// ./src/app/page/login-page/login-page.component.ts